import { useQuery } from '@tanstack/react-query';
import { getValueSavings, getActivitySummary, getValueCompliance } from '../value.api';

export interface ValueSummaryData {
  speedToShelf: {
    newProductsCount: number;
    avgLeadTimeDays: number | null;
  };
  compliance: {
    gs1UpdatesHandled: number;
    ppwrStatus: 'compliant' | 'pending' | 'not_applicable';
  };
  correctness: {
    productsManagedCount: number | null;
    openBlockersCount: number;
    retailerQuestionsHandled: number;
  };
}

function mapPpwrStatus(status: string): 'compliant' | 'pending' | 'not_applicable' {
  if (status === 'prepared') return 'compliant';
  if (status === 'mostly_prepared' || status === 'action_needed') return 'pending';
  return 'not_applicable';
}

export function useValueSummary(gln: string) {
  const savingsQuery = useQuery({
    queryKey: ['value', 'savings', gln],
    queryFn: () => getValueSavings(gln),
    enabled: !!gln,
    staleTime: 5 * 60 * 1000,
  });

  const activitiesQuery = useQuery({
    queryKey: ['value', 'activities-summary', gln],
    queryFn: () => getActivitySummary(gln, 'quarter'),
    enabled: !!gln,
    staleTime: 5 * 60 * 1000,
  });

  const complianceQuery = useQuery({
    queryKey: ['value', 'compliance', gln],
    queryFn: () => getValueCompliance(gln),
    enabled: !!gln,
    staleTime: 5 * 60 * 1000,
  });

  const savings = savingsQuery.data?.data;
  const activities = activitiesQuery.data?.data;
  const compliance = complianceQuery.data?.data;

  // Only construct data when at least one source has loaded successfully
  const hasAnyData = savings || activities || compliance;
  const data: ValueSummaryData | null = hasAnyData ? {
    speedToShelf: {
      newProductsCount: activities?.product_launched ?? 0,
      avgLeadTimeDays: savings?.timeToMarket.withXxtractDays ?? null,
    },
    compliance: {
      gs1UpdatesHandled: activities?.gs1_update ?? 0,
      ppwrStatus: compliance ? mapPpwrStatus(compliance.ppwr.status) : 'not_applicable',
    },
    correctness: {
      productsManagedCount: savings?.totalProducts ?? null,
      openBlockersCount: 0,
      retailerQuestionsHandled: activities?.retailer_question ?? 0,
    },
  } : null;

  return {
    data,
    isLoading: savingsQuery.isLoading || activitiesQuery.isLoading || complianceQuery.isLoading,
    // Report error only when ALL queries have failed — partial success is acceptable (graceful degradation)
    isError: savingsQuery.isError && activitiesQuery.isError && complianceQuery.isError,
    hasPartialError: savingsQuery.isError || activitiesQuery.isError || complianceQuery.isError,
  };
}
